<template>
	<div>
		<MrrBanner v-if="displayMrrBanner" :adPosition="adPosition" />
		<div
			v-else
			class="ad-unit"
			v-observe-visibility="onVisibleCallback"
			ref="adUnit"
			@click="onAdClick()"
			@mouseleave="onMouseLeaveAd()"
		>
			<template v-if="isAdSpaceShown">
				<div :id="`slot-${adPosition}`" class="ad-container ad-unit--height" :data-ad-position="adPosition">
					<div>
						<ClientOnly>
							<div v-if="adProvider === 'yieldlove'" class="ad-unit__container" :id="slotId" />
							<div v-if="adProvider === 'GAM'" class="ad-unit__container" :id="slotId" />

							<div v-else-if="adProvider === 'adsense'" class="ad-unit__container" :id="slotId">
								<ins
									class="ad-div adsbygoogle"
									:data-ad-client="config.adClient"
									:data-ad-slot="slotId"
									:style="adsenseStyle"
								></ins>
							</div>
						</ClientOnly>

						<div class="ad-unit__removal-container">
							<button v-if="isShown && allowAdRemoval" class="ad-unit__removal" @click.stop="openModal">
								<FontAwesomeIcon :icon="faTimes" />
								<span> {{ $t('WEBAPP_LOGINS_REMOVE') }} </span>
							</button>
						</div>
					</div>
				</div>
			</template>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, onUnmounted, ref, watch } from 'vue';
import { AccountModal, ProModal } from '@/lazy-load-components';
import { ImpressionTrackingEvents } from '@/enums/events';
import { AdProvider, adSlots, getAdProvider, loadAd, removeAd } from '@/helpers/ad/ad-helper';
import type { TitleDetail } from '@/interfaces/title-details-graphql';

import { getYieldloveMaxSlotSize } from '@/helpers/ad/yieldlove-helper';
import { ModalHelper } from '@/helpers/vfm/modal-helper';
import { isProWebCountry, saveLastProView } from '@/helpers/pro-web-helper';
import { SnowplowContext } from '@/helpers/tracking/providers';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { TrafficHelper } from '@/helpers/traffic-helper';
import { getViewport } from '@/helpers/ui-helper';
import { lowPriorityCallback } from '@/helpers/request-idle-helper';

import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { useUserStore } from '@/stores/pinia/useUserStore';
import { useRoutingStore } from '@/stores/pinia/useRoutingStore';
import { useAppStore } from '@/stores/pinia/useAppStore';
import { storeToRefs } from 'pinia';
import { hasUCConsentBanner } from '@/helpers/tracking';
import { until } from '@vueuse/core';
const MrrBanner = defineAsyncComponent(() => import('@/components/banners/MrrBanner.vue'));

const { isUsingAdBlocker } = storeToRefs(useAppStore());

interface TrackEventArgs {
	action?: 'click' | 'impression';
	label: string;
	property?: string;
	nonInteraction?: boolean;
	contexts?: SnowplowContext[];
}

/**
 * the component will be blocking some ad space already on the server.
 * the server doesn't know the viewport of the user, therefore it assumes 420px (yiellove-helper.ts:99).
 * the first 50 percentile are between 320-420px. so we priotize mobile first.
 */

const props = withDefaults(
	defineProps<{
		adPosition: string;
		allowAdRemoval?: boolean;
		additionalContexts?: SnowplowContext[];
		subgenres?: TitleDetail['content']['subgenres'];
	}>(),
	{
		allowAdRemoval: true,
		additionalContexts: () => [],
	}
);

const emits = defineEmits<{
	(e: 'adClicked'): void;
	(e: 'removeAdClicked'): void;
}>();

const iframeAdProviders = [AdProvider.YIELDLOVE, AdProvider.ADSENSE, AdProvider.GAM];
const onVisibleCallback = {
	callback: (isVisible: boolean, entry: IntersectionObserverEntry) => onAdImpression(isVisible, entry),
};

const { webLocale, country } = storeToRefs(useLanguageStore());
const { activeRoute, urlMetadata } = storeToRefs(useRoutingStore());
const { jwLoginId, isPremium, strictlyAllConsentsGranted } = storeToRefs(useUserStore());

const isSlotFilled = ref<null | boolean>(null);
const isShown = ref(false);
const adUnit = ref<HTMLElement | null>(null);

const adProvider = computed(() => {
	const isSportAdUnit = props.adPosition.includes('sport');
	return getAdProvider(webLocale.value, isSportAdUnit);
});

/** slots */
const yieldloveSlotId = computed<string>(() => `jw_${webLocale.value}_${props.adPosition}`);
const adSlot = computed(() => {
	const max = getYieldloveMaxSlotSize(
		webLocale.value,
		yieldloveSlotId.value,
		adProvider.value === AdProvider.YIELDLOVE
	);

	const adSlot = adSlots
		.filter(adSlot => adSlot.name === `jw_{WEBLOCALE}_${props.adPosition}`)
		.map(adSlot => adSlot.sizes)
		// flatten
		.reduce((root: any, item: any) => {
			root = root.concat(item);
			return root;
		}, [])
		.find((slot: any) => slot.x === max.x && slot.y === max.y);
	return adSlot || {};
});
const slotId = computed<string>(() => {
	switch (adProvider.value) {
		case AdProvider.YIELDLOVE:
		case AdProvider.GAM:
			return yieldloveSlotId.value;
		case AdProvider.ADSENSE:
			return adSlot.value.adsenseSlot;
		default:
			return '';
	}
});

// @todo would be great to refactor these ads to only use 1 config instead of so many different ones
const config = computed(() => {
	switch (adProvider.value) {
		case AdProvider.ADSENSE:
			return {
				adClient: 'ca-pub-3179068936475881',
			};

		default:
			return {};
	}
});
const adsenseStyle = computed(() => {
	return {
		display: 'inline-block',
		width: adSlot.value.x + 'px',
		height: adSlot.value.y + 'px',
	};
});
const isAdSpaceShown = computed(() => {
	const userStatus = isProWebCountry(webLocale.value) ? !isPremium.value : !jwLoginId.value;
	return (
		userStatus &&
		isCorrectViewport() &&
		!TrafficHelper.isCampaignTraffic() &&
		!urlMetadata?.value?.htmlContentJson?.exclude_from_ads
	);
});

/**
 * MRR Banner
 * this is a JW ad banner, used in the case where the ad is not filled or the user has an ad blocker
 */
const isTitlePageSlot = computed(() => ['under_buybox', 'under_detail_info', 'bottom_page'].includes(props.adPosition));

const displayMrrBanner = computed(() => {
	return isAdSpaceShown.value && isTitlePageSlot.value && (isSlotFilled.value === false || isUsingAdBlocker.value);
});
// MRR_BANNER

const targeting = computed(() => {
	// Temporarily limit to one market for subgenres until we can validate data
	if (![AdProvider.YIELDLOVE, AdProvider.GAM].includes(adProvider.value) || !props.subgenres?.length) return null;

	const subgenreShortNames = props.subgenres.map(s => s.content.shortName).filter(s => s != null);
	if (!subgenreShortNames.length) return null;

	return { subgenre: subgenreShortNames.join(',') };
});

function isAdAtTop(): boolean {
	return (
		[AdProvider.YIELDLOVE, AdProvider.ADSENSE, AdProvider.GAM].includes(adProvider.value) &&
		props.adPosition === 'top'
	);
}

/**
 * check ad unit `top` is in smartphone viewport.
 */
function isCorrectViewport(_stateName = activeRoute.value?.name) {
	return getViewport() !== 'smartphone' || !isAdAtTop();
}

/** Main point to set ad as shown. */
async function setShown() {
	const localIsShown = isAdSpaceShown.value;

	if (import.meta.client) {
		// note: non-UC countries (regarding consent) should load the ads immediately.
		// that's why only UC countries will wait for `CONSENT_PARTNERS_ANSWERED`.
		if (hasUCConsentBanner(webLocale.value)) await until(strictlyAllConsentsGranted).toBeTruthy();

		// the server will block the ads in SSR and the client will then load the ads into that space
		// this way we prevent cumulative layout shift
		if (!isShown.value && localIsShown) {
			await loadAd(
				webLocale.value,
				slotId.value,
				adProvider.value,
				filled => (isSlotFilled.value = filled),
				targeting.value
			);
		} else if (isShown.value && !localIsShown) {
			await removeAd(webLocale.value, slotId.value, adProvider.value);
		}
	}

	isShown.value = localIsShown;
}

function onAdImpression(isVisible: boolean, _entry: any) {
	if (!isVisible || !isAdSpaceShown.value) return;

	const event: TrackEventArgs = { action: 'impression', label: 'banner', nonInteraction: true };
	TrackingHelper.trackEvent(ImpressionTrackingEvents.AD, event, props.additionalContexts);
}

function onAdClick() {
	const event: TrackEventArgs = { action: 'click', label: 'banner' };
	TrackingHelper.trackEvent(ImpressionTrackingEvents.AD, event, props.additionalContexts);

	emits('adClicked');
}

function triggerAdClickOnBlur() {
	if (adUnit.value?.querySelector('iframe') === document.activeElement) {
		onAdClick();
	}
}

function onMouseLeaveAd() {
	/**
	 * once the user clicks on the ad the window loses focus and we will not be able to track the second click
	 * unless the user clicks outside the Ad , to fix this issue once the mouse leaves the Ad the window
	 * will gain focus.
	 */
	if (adUnit.value?.querySelector('iframe') === document.activeElement) {
		window.focus();
	}
}

function openModal() {
	const location = 'RemoveAds';
	if (isProWebCountry(webLocale.value)) {
		ModalHelper.openModal(ProModal, {}, {}, location, true);
		saveLastProView(location);
	} else {
		ModalHelper.openModal(AccountModal, { closable: true }, { cssClass: 'account-modal' }, 'AdUnit');
	}
	emits('removeAdClicked');
}

watch(
	() => [activeRoute.value, jwLoginId.value],
	async () => await setShown()
);

onMounted(() => {
	lowPriorityCallback(setShown);

	/**
	 * for ads which are being rendered in an iframe we will not be able to track the clicks using
	 * a simple onClick event, that's why we added the "blur" listener which will trigger the AdClick
	 * event once the window loses focus (when the user click on the Ad) and the new active element
	 * is the iframe withing this adUnit check the triggerAdClickOnBlur function.
	 *
	 * we will keep the onClick event for the other ads like banners which are not being rendered in a iframe,
	 * and no need for the blur listener for those.
	 */
	if (iframeAdProviders.includes(adProvider.value)) {
		window.addEventListener('blur', triggerAdClickOnBlur);
	}
});

onBeforeUnmount(() => {
	window.removeEventListener('blur', triggerAdClickOnBlur);
});

onUnmounted(async () => {
	if (hasUCConsentBanner(webLocale.value)) {
		await until(strictlyAllConsentsGranted).toBeTruthy();
	}

	removeAd(webLocale.value, slotId.value, adProvider.value);
});
</script>

<style lang="scss" scoped>
@import '~@/styles/variables.scss';
.ad-unit {
	display: flex;
	flex-direction: column;
	align-items: center;

	&__container {
		overflow: hidden;
		text-align: center;

		&--button {
			all: unset;
			cursor: pointer;
		}

		div {
			iframe {
				vertical-align: middle !important;
			}
		}
	}
	&__removal {
		all: unset;
		margin-top: 5px;
		min-height: 22px;
		cursor: pointer;
		color: var(--jw-color-secondary);
	}
	&__removal-container {
		position: absolute;
		bottom: -25px;
		width: 100%;
		display: flex;
		justify-content: center;
	}
}

div.ad-container {
	position: relative;
	margin-bottom: 30px;
	margin-top: 10px;

	/* Data ad positions comes from `adSlots` in `ad-helper.ts` */

	&[data-ad-position='top'] {
		height: 50px;
		width: 320px;

		@media (max-width: 825px) {
			height: 90px;
			width: 728px;
		}
	}

	&[data-ad-position='inlist_1'],
	&[data-ad-position='inlist_2'],
	&[data-ad-position='inlist_3'] {
		margin-top: 30px;
	}

	&[data-ad-position='inlist_1'],
	&[data-ad-position='inlist_2'],
	&[data-ad-position='inlist_3'],
	&[data-ad-position='sport_inlist_1'],
	&[data-ad-position='sport_inlist_2'],
	&[data-ad-position='sport_inlist_3'],
	&[data-ad-position='sport_inlist_4'],
	&[data-ad-position='sport_inlist_5'],
	&[data-ad-position='sport_inlist_6'],
	&[data-ad-position='sport_inlist_7'] {
		align-self: flex-start;
		height: 250px;
		width: 970px;

		@media (max-width: 1106px) {
			width: 300px;
		}
	}

	&[data-ad-position='under_detail_info'] {
		height: 600px;
		width: 300px;
	}

	&[data-ad-position='under_buybox'],
	&[data-ad-position='bottom_page'],
	&[data-ad-position='sport_under_offers'],
	&[data-ad-position='sport_under_details'] {
		height: 90px;
		width: 728px;

		@media (max-width: 1130px) {
			height: 250px;
			width: 300px;
		}
	}
}
</style>
